import React, { useEffect, useRef } from 'react';
import { RelBox } from '../../../api/ai.api.types';
import { rampColor } from '../helper/alignmentGuidance';

interface AlignmentParticlesProps {
    box: RelBox | null;
    targetBox: RelBox;
    score: number;
    snapped: boolean;
}

interface Particle {
    // Viewport-relative (0..1) start/end; position is eased between them over the lifetime.
    sx: number;
    sy: number;
    tx: number;
    ty: number;
    born: number;
    life: number;
    radius: number;
}

const SPAWN_INTERVAL_MS = 45;
const SPAWN_PER_TICK = 3;
const MAX_PARTICLES = 110;
const SNAP_BURST_COUNT = 32;

const easeInOutCubic = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

// A random point on the outline of a box (uniformly by perimeter side choice).
const outlinePoint = (box: RelBox): { x: number; y: number } => {
    const side = Math.floor(Math.random() * 4);
    const u = Math.random();
    switch (side) {
        case 0:
            return { x: box.x + u * box.w, y: box.y };
        case 1:
            return { x: box.x + u * box.w, y: box.y + box.h };
        case 2:
            return { x: box.x, y: box.y + u * box.h };
        default:
            return { x: box.x + box.w, y: box.y + u * box.h };
    }
};

// Particle stream flowing from the (tracked) meter box toward the target frame — an organic,
// animated way of showing which on-screen motion the user should cause (pan the camera so the
// box follows the stream). Each particle starts on the meter-box outline and travels to the
// corresponding point of the target frame, so the stream visually "morphs" the box into the
// frame. On snap the stream stops and a short absorption burst plays instead. Rendered on a
// canvas so the ~30-60fps animation never touches React rendering. Honors
// prefers-reduced-motion by not rendering anything.
const AlignmentParticles = ({ box, targetBox, score, snapped }: AlignmentParticlesProps) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const boxRef = useRef(box);
    boxRef.current = box;
    const targetBoxRef = useRef(targetBox);
    targetBoxRef.current = targetBox;
    const scoreRef = useRef(score);
    scoreRef.current = score;
    const snappedRef = useRef(snapped);

    // Snap edge: burst instead of stream (handled inside the loop via this flag).
    const burstPendingRef = useRef(false);
    useEffect(() => {
        if (snapped && !snappedRef.current) {
            burstPendingRef.current = true;
        }
        snappedRef.current = snapped;
    }, [snapped]);

    useEffect(() => {
        const reducedMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx || reducedMotion) {
            return undefined;
        }

        let cancelled = false;
        let rafHandle = 0;
        let lastSpawn = 0;
        const particles: Particle[] = [];

        const resize = () => {
            const dpr = window.devicePixelRatio || 1;
            canvas.width = canvas.clientWidth * dpr;
            canvas.height = canvas.clientHeight * dpr;
        };
        resize();
        window.addEventListener('resize', resize);

        const spawnStream = (now: number, sourceBox: RelBox) => {
            const target = targetBoxRef.current;
            for (let i = 0; i < SPAWN_PER_TICK && particles.length < MAX_PARTICLES; i++) {
                const start = outlinePoint(sourceBox);
                // Corresponding point on the target frame (same normalized outline position
                // semantics: map via the relative position inside the box).
                const nx = (start.x - sourceBox.x) / (sourceBox.w || 1);
                const ny = (start.y - sourceBox.y) / (sourceBox.h || 1);
                particles.push({
                    sx: start.x,
                    sy: start.y,
                    tx: target.x + nx * target.w,
                    ty: target.y + ny * target.h,
                    born: now,
                    life: 700 + Math.random() * 400,
                    radius: 2.5 + Math.random() * 2.5,
                });
            }
        };

        const spawnBurst = (now: number, sourceBox: RelBox) => {
            for (let i = 0; i < SNAP_BURST_COUNT && particles.length < MAX_PARTICLES + SNAP_BURST_COUNT; i++) {
                const target = outlinePoint(sourceBox);
                const centerX = sourceBox.x + sourceBox.w / 2;
                const centerY = sourceBox.y + sourceBox.h / 2;
                // Start slightly outside the outline point (radially away from the center) and
                // get absorbed into the frame.
                particles.push({
                    sx: target.x + (target.x - centerX) * 0.25,
                    sy: target.y + (target.y - centerY) * 0.25,
                    tx: target.x,
                    ty: target.y,
                    born: now,
                    life: 280 + Math.random() * 120,
                    radius: 2.5 + Math.random() * 3,
                });
            }
        };

        const tick = (now: number) => {
            if (cancelled) {
                return;
            }
            rafHandle = window.requestAnimationFrame(tick);

            const currentBox = boxRef.current;
            if (burstPendingRef.current && currentBox) {
                burstPendingRef.current = false;
                spawnBurst(now, currentBox);
            }
            if (currentBox && !snappedRef.current && now - lastSpawn >= SPAWN_INTERVAL_MS) {
                lastSpawn = now;
                spawnStream(now, currentBox);
            }

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            const color = rampColor(scoreRef.current);
            for (let i = particles.length - 1; i >= 0; i--) {
                const p = particles[i];
                const t = (now - p.born) / p.life;
                if (t >= 1) {
                    particles.splice(i, 1);
                    continue;
                }
                const eased = easeInOutCubic(t);
                const x = (p.sx + (p.tx - p.sx) * eased) * canvas.width;
                const y = (p.sy + (p.ty - p.sy) * eased) * canvas.height;
                const fade = Math.sin(Math.PI * t);
                const radius = p.radius * (window.devicePixelRatio || 1);
                // Soft halo + solid core — reads as a glow without the (slow) canvas shadowBlur.
                ctx.fillStyle = color;
                ctx.globalAlpha = fade * 0.3;
                ctx.beginPath();
                ctx.arc(x, y, radius * 2.2, 0, Math.PI * 2);
                ctx.fill();
                ctx.globalAlpha = fade;
                ctx.beginPath();
                ctx.arc(x, y, radius, 0, Math.PI * 2);
                ctx.fill();
            }
            ctx.globalAlpha = 1;
        };

        rafHandle = window.requestAnimationFrame(tick);
        return () => {
            cancelled = true;
            window.cancelAnimationFrame(rafHandle);
            window.removeEventListener('resize', resize);
        };
    }, []);

    return <canvas ref={canvasRef} data-testid="alignment-particles" className={'absolute inset-0 w-full h-full pointer-events-none'} aria-hidden={true} />;
};

export default AlignmentParticles;
